import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import api from '../../services/api';

interface DeviceAuditTabProps {
  deviceId: string;
}

const PAGE_SIZE = 15;

function formatAction(action: string): string {
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDetails(details: Record<string, unknown>): string {
  if (!details || Object.keys(details).length === 0) return '—';

  // Try common detail fields for a human-readable summary
  if (details.message && typeof details.message === 'string') {
    return details.message;
  }
  if (details.description && typeof details.description === 'string') {
    return details.description;
  }
  if (details.reason && typeof details.reason === 'string') {
    return details.reason;
  }

  // Fallback: show key=value pairs
  const parts = Object.entries(details)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .slice(0, 3);

  return parts.join(', ') || '—';
}

function DeviceAuditTab({ deviceId }: DeviceAuditTabProps) {
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['device-audit-log', deviceId, page],
    queryFn: () => api.adminGetDeviceAuditLog(deviceId),
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;

  // Derive unique action types for the filter dropdown
  const actionTypes = useMemo(() => {
    const types = new Set(logs.map((log) => log.action));
    return Array.from(types).sort();
  }, [logs]);

  // Apply client-side action filter
  const filteredLogs = useMemo(() => {
    if (actionFilter === 'all') return logs;
    return logs.filter((log) => log.action === actionFilter);
  }, [logs, actionFilter]);

  // Paginate the filtered results
  const paginatedLogs = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredLogs.slice(start, start + PAGE_SIZE);
  }, [filteredLogs, page]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));

  // Reset page when filter changes
  const handleFilterChange = (value: string) => {
    setActionFilter(value);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      {/* Filter controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-200 uppercase tracking-wider">
          Audit Log
        </h3>
        <div className="flex items-center gap-2">
          <label htmlFor="action-filter" className="text-xs text-slate-400">
            Filter by action:
          </label>
          <select
            id="action-filter"
            value={actionFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-200 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="all">All actions</option>
            {actionTypes.map((action) => (
              <option key={action} value={action}>
                {formatAction(action)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 bg-slate-800">
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Timestamp
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Action
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Details
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  Actor
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                // Skeleton loading rows
                Array.from({ length: 5 }).map((_, i) => (
                  <tr
                    key={`skeleton-${i}`}
                    className={i % 2 === 0 ? 'bg-slate-800/30' : 'bg-slate-800/10'}
                  >
                    <td className="px-4 py-3">
                      <div className="h-4 w-36 animate-pulse rounded bg-slate-700" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-28 animate-pulse rounded bg-slate-700" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-48 animate-pulse rounded bg-slate-700" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="h-4 w-32 animate-pulse rounded bg-slate-700" />
                    </td>
                  </tr>
                ))
              ) : paginatedLogs.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-12 text-center text-sm text-slate-500"
                  >
                    No audit log entries for this device
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((log, index) => (
                  <tr
                    key={log.id}
                    className={
                      index % 2 === 0
                        ? 'bg-slate-800/30 hover:bg-slate-700/40'
                        : 'bg-slate-800/10 hover:bg-slate-700/40'
                    }
                  >
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {format(new Date(log.created_at), 'd MMM yyyy HH:mm:ss')}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center rounded-full bg-slate-700/60 px-2.5 py-0.5 text-xs font-medium text-slate-200">
                        {formatAction(log.action)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-300 max-w-xs truncate" title={formatDetails(log.details)}>
                      {formatDetails(log.details)}
                    </td>
                    <td className="px-4 py-3 text-slate-300 whitespace-nowrap">
                      {log.performed_by || 'system'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      {!isLoading && filteredLogs.length > 0 && (
        <div className="flex items-center justify-between pt-2">
          <p className="text-xs text-slate-400">
            Showing {(page - 1) * PAGE_SIZE + 1}–
            {Math.min(page * PAGE_SIZE, filteredLogs.length)} of{' '}
            {filteredLogs.length} entries
            {actionFilter !== 'all' && ` (${total} total)`}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
            >
              Previous
            </button>
            <span className="text-xs text-slate-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="rounded-md border border-slate-600 bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-300 hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-40 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceAuditTab;
